import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Power, PowerOff } from 'lucide-react';
import api from '../../services/api';
import Modal from '../Modal';
import { formatMonto } from '../../utils/format';

// Catálogo editable de conceptos de retención ISLR (#35/#97). No hay
// eliminar real (retencion.islr_codigo_id tiene FK hacia acá) — se
// desactiva en vez de borrar, igual que "Inhabilitar" en Empresas.
// Vive en Contabilidad → Configuración contable; lo mantiene el contador
// (o superior), igual que exige el backend.
// porcentaje/sustraendo = persona natural; *_juridica = persona jurídica.
// La factura usa uno u otro según la letra del RIF del proveedor.
const FORM_VACIO = { codigo: '', concepto: '', porcentaje: '', sustraendo: '', porcentaje_juridica: '', sustraendo_juridica: '' };
const aTexto = (v) => (v != null ? String(Number(v)) : '');

export default function RetencionIslrCodigos() {
  const [codigos, setCodigos] = useState([]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [error, setError] = useState('');

  function cargar() {
    api.get('/retencion-islr-codigos').then(({ data }) => setCodigos(data));
  }

  useEffect(() => {
    cargar();
  }, []);

  function empezarEdicion(c) {
    setEditandoId(c.id);
    setForm({
      codigo: c.codigo,
      concepto: c.concepto,
      porcentaje: aTexto(c.porcentaje),
      sustraendo: aTexto(c.sustraendo),
      porcentaje_juridica: aTexto(c.porcentaje_juridica),
      sustraendo_juridica: aTexto(c.sustraendo_juridica)
    });
    setMostrarForm(true);
  }

  function cancelar() {
    setMostrarForm(false);
    setEditandoId(null);
    setForm(FORM_VACIO);
    setError('');
  }

  async function guardar(e) {
    e.preventDefault();
    setError('');
    try {
      if (editandoId) {
        await api.put(`/retencion-islr-codigos/${editandoId}`, form);
      } else {
        await api.post('/retencion-islr-codigos', form);
      }
      cancelar();
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar el concepto');
    }
  }

  async function toggleActivo(c) {
    const accion = c.activo ? 'Inhabilitar' : 'Habilitar';
    if (!window.confirm(`¿${accion} el concepto "${c.concepto}"?`)) return;
    await api.put(`/retencion-islr-codigos/${c.id}`, { activo: !c.activo });
    cargar();
  }

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between p-4 pb-3">
        <div>
          <h2 className="font-semibold text-slate-700">Retención ISLR — Conceptos</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            % y sustraendo de cada concepto para persona natural y jurídica (Art. 9 Decreto 1808) — cárgalos con tu contador.
            La factura toma uno u otro según la letra del RIF del proveedor (V, E, P natural; J, G, C jurídica).
          </p>
        </div>
        <button
          onClick={() => setMostrarForm(true)}
          title="Nuevo concepto"
          className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-md transition-colors"
        >
          <Plus size={16} />
        </button>
      </div>

      {mostrarForm && (
        <Modal title={editandoId ? 'Editando concepto' : 'Nuevo concepto'} onClose={cancelar}>
          <form onSubmit={guardar} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              required
              placeholder="Código (ej. HP)"
              value={form.codigo}
              onChange={(e) => setForm({ ...form, codigo: e.target.value })}
              className="border rounded-md px-3 py-2"
            />
            <input
              required
              placeholder="Concepto"
              value={form.concepto}
              onChange={(e) => setForm({ ...form, concepto: e.target.value })}
              className="border rounded-md px-3 py-2"
            />
            {[
              ['Persona natural', 'porcentaje', 'sustraendo'],
              ['Persona jurídica', 'porcentaje_juridica', 'sustraendo_juridica']
            ].map(([titulo, pct, sus]) => (
              <fieldset key={pct} className="sm:col-span-2 grid grid-cols-2 gap-3 rounded-md border border-slate-200 p-3">
                <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{titulo}</legend>
                <label className="text-sm text-slate-600 flex flex-col gap-1">
                  % de retención
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Vacío si no se conoce aún"
                    value={form[pct]}
                    onChange={(e) => setForm({ ...form, [pct]: e.target.value })}
                    className="border rounded-md px-3 py-2"
                  />
                </label>
                <label className="text-sm text-slate-600 flex flex-col gap-1">
                  Sustraendo (opcional)
                  <input
                    type="number"
                    step="0.01"
                    value={form[sus]}
                    onChange={(e) => setForm({ ...form, [sus]: e.target.value })}
                    className="border rounded-md px-3 py-2"
                  />
                </label>
              </fieldset>
            ))}
            {error && <p className="text-red-600 text-sm sm:col-span-2">{error}</p>}
            <button type="submit" className="bg-primary-600 text-white rounded-md py-2 sm:col-span-2 hover:bg-primary-700">
              {editandoId ? 'Guardar cambios' : 'Guardar'}
            </button>
          </form>
        </Modal>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="p-3">Código</th>
              <th className="p-3">Concepto</th>
              <th className="p-3 text-right">Natural %</th>
              <th className="p-3 text-right">Sustraendo</th>
              <th className="p-3 text-right">Jurídica %</th>
              <th className="p-3 text-right">Sustraendo</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {codigos.map((c) => (
              <tr key={c.id} className={`border-t hover:bg-slate-50 transition-colors ${!c.activo ? 'opacity-50' : ''}`}>
                <td className="p-3 font-mono whitespace-nowrap">{c.codigo}</td>
                <td className="p-3">{c.concepto}</td>
                {[[c.porcentaje, c.sustraendo], [c.porcentaje_juridica, c.sustraendo_juridica]].map(([pct, sus], k) => (
                  <React.Fragment key={k}>
                    <td className="p-3 text-right whitespace-nowrap">
                      {pct != null ? `${formatMonto(pct)}%` : <span className="text-amber-600">Falta cargar</span>}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">{sus != null ? formatMonto(sus) : '-'}</td>
                  </React.Fragment>
                ))}
                <td className="p-3">
                  <div className="flex items-center gap-3 justify-end">
                    <button onClick={() => empezarEdicion(c)} title="Editar" className="text-slate-400 hover:text-primary-600">
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => toggleActivo(c)}
                      title={c.activo ? 'Inhabilitar' : 'Habilitar'}
                      className={c.activo ? 'text-slate-400 hover:text-red-600' : 'text-slate-400 hover:text-green-600'}
                    >
                      {c.activo ? <Power size={16} /> : <PowerOff size={16} />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {codigos.length === 0 && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-slate-400">Sin conceptos creados.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
